import React, { useContext, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import './index.scss'
import Button from '@Components/Buttons/Button'
import MatchRecordingGroups from '@Components/MatchComponents/MatchRecordingGroups'
import ModalPortal from '@App/kit/components/Popups/ModalPortal'
import { StoresContext } from '@App/contexts'
import ContentHeader from '@Components/ContentHeader'
import RootStore from '@App/stores/root-store'
import SearchPanel from '@Components/Search/SearchPanel'
import { FormFieldTypes } from '@App/enum/form-field-types'
import MatchSearchHeader from '@Scenes/RightsholderScene/MatchClaim/Match/MatchSearch/MatchSearchHeader'
import MatchWorksPopup from '@Components/MatchComponents/MatchWorksPopup'
import { sourceValues, unmatchedValueOptions } from '@Const/options/dropdown-options'
import Pluralize from 'pluralize'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import { MatchSearchFilter, MatchSearchParameters } from '@App/stores/rightsholder/match/matching-store'
import { createUsagePeriodFilter } from '@App/forms/filters/usage-period-filter'
import SubmitMatchPopup from '@Components/MatchComponents/SubmitMatchPopup'
import usePreselectedMatchWork from '@Scenes/RightsholderScene/MatchClaim/Match/use-preselected-match-work'
import useSubmitMatch from '@Scenes/RightsholderScene/MatchClaim/Match/use-submit-match'
import PreselectedWorkCard from '@Scenes/RightsholderScene/MatchClaim/Match/MatchSearch/PreselectedWorkCard'
import BackButton from '@Components/Buttons/BackButton'
import MatchSearchTitle from '@Scenes/RightsholderScene/MatchClaim/Match/MatchSearch/MatchSearchTitle'
import useFilterUrlSync from '@Components/Search/SearchPanel/useFilterUrlSync'
import useSearchCriteriaUrlSync from '@Components/Search/SearchPanel/useSearchCriteriaUrlSync'
import { useLocation } from 'react-router'
import { MatchingToolLocationState } from '@Entities/matching/preselected-match-work'

interface MatchSearchProps {
    goToStep: (step: number) => void
}

const MatchSearch = (props: MatchSearchProps) => {
    const { matchingStore } = useContext<RootStore>(StoresContext)
    const { goToStep } = props

    const [loading, setLoading] = useState<boolean>(false)
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
    const [submittingMatch, setSubmittingMatch] = useState<boolean>(false)

    const submitMatch = useSubmitMatch()

    const preselectedWork = matchingStore.preselectedWork
    const recordingGroupsCount = matchingStore.selectedUnmatchedRecordingGroups.length
    const matchSearchFilter = useMemo<Array<FilterFieldGroup<MatchSearchFilter>>>(
        () => [
            {
                name: 'Estimated Value',
                allowMultiple: false,
                filterFields: [
                    {
                        claim: {
                            type: FormFieldTypes.RADIO,
                            name: 'unmatchedValue',
                            label: 'Estimated Value',
                            options: unmatchedValueOptions,
                        },
                    },
                ],
            },
            {
                name: 'Source',
                allowMultiple: false,
                filterFields: [
                    {
                        claim: {
                            type: FormFieldTypes.RADIO,
                            name: 'usageSource',
                            label: 'Source',
                            options: sourceValues,
                        },
                    },
                ],
            },
            {
                name: 'Usage Period Month',
                allowMultiple: false,
                filterFields: [
                    {
                        usagePeriod: createUsagePeriodFilter(),
                    },
                ],
            },
        ],
        []
    )

    // No auto-search runs on mount here (unless a preselected work seeds one, below), so this only
    // pre-populates the filter UI for the user's own next search; the URL wins over whatever
    // matchingFilterCriteria the store already holds.
    const { urlFilterValues, syncFiltersToUrl } = useFilterUrlSync<MatchSearchFilter>(matchSearchFilter)

    // A link can carry search terms (?artist=…). Seeded before the first render — the form picks its rows up then —
    // and only on a fresh visit: not when a preselected work is driving the search, and not when results are
    // already held (coming back from the next step), which would throw away the user's selection.
    const { urlCriteria, syncCriteriaToUrl } = useSearchCriteriaUrlSync(
        matchingStore.searchCriteriaStore.criteriaOptions
    )
    const location = useLocation()
    const [hasLinkedSearch] = useState(() => {
        const hasPreselectedWork = !!(location.state as MatchingToolLocationState | null)?.matchWork
        return (
            !!urlCriteria &&
            !hasPreselectedWork &&
            !matchingStore.unmatchedSearchResults &&
            matchingStore.seedSearchFromUrl(urlCriteria, urlFilterValues as Partial<MatchSearchFilter> | undefined)
        )
    })

    const newSearch = async (searchCriteria: Partial<MatchSearchParameters>, filter: Partial<MatchSearchFilter>) => {
        syncCriteriaToUrl(searchCriteria)
        setLoading(true)
        matchingStore.setMatchingSearchCriteria(searchCriteria)
        matchingStore.setMatchingFilterCriteria(filter)
        await matchingStore.loadUnmatchedSearchResults(1)
        setLoading(false)
    }

    const moreResults = async () => {
        setLoading(true)
        await matchingStore.loadNextUnmatchedSearchResultsPage()
        setLoading(false)
    }

    const matchToPreselectedWork = async () => {
        if (!preselectedWork) {
            return
        }
        setSubmittingMatch(true)
        try {
            await submitMatch(preselectedWork.songCode, matchingStore.selectedUnmatchedRecordingGroups)
            goToStep(3)
        } finally {
            setSubmittingMatch(false)
        }
    }

    usePreselectedMatchWork(matchingStore, (work) => newSearch({ recordingTitle: work.title }, {}))

    useEffect(() => {
        analyticsSendPage('rightsholder_match_search')
        if (hasLinkedSearch) {
            newSearch(matchingStore.matchingSearchCriteria, matchingStore.matchingFilterCriteria)
        }
    }, [])

    return (
        <>
            <title>Matching Tool</title>
            <div className="match-search-wrapper">
                <ContentHeader
                    title={<MatchSearchTitle work={preselectedWork} />}
                    descriptionElement={<MatchSearchHeader />}
                    navLeft={preselectedWork && <BackButton />}
                    navRight={
                        preselectedWork && (
                            <Button mainColor type="primary" onClick={() => matchingStore.resetStore()}>
                                Remove
                            </Button>
                        )
                    }
                />
                <div className="matching-tool">
                    {preselectedWork && <PreselectedWorkCard work={preselectedWork} />}
                    <SearchPanel
                        key={preselectedWork?.songCode ?? 'no-preselected-work'}
                        searchCriteriaStore={matchingStore.searchCriteriaStore}
                        canAddCriteria
                        onSearch={newSearch}
                        disabled={loading}
                        filterFieldCollections={matchSearchFilter}
                        defaultFilterValues={urlFilterValues ?? matchingStore.matchingFilterCriteria}
                        onFilterValueChange={syncFiltersToUrl}
                    />
                    <div className="match-claim-table">
                        <MatchRecordingGroups loadMore={moreResults} loading={loading} />
                    </div>
                    {recordingGroupsCount > 0 && (
                        <Button mainColor className="match-button" type="primary" onClick={() => setModalIsOpen(true)}>
                            Match {Pluralize('Group', recordingGroupsCount, true)}
                        </Button>
                    )}
                    <ModalPortal isOpen={modalIsOpen}>
                        {preselectedWork ? (
                            <SubmitMatchPopup
                                isLoading={submittingMatch}
                                onCancel={() => setModalIsOpen(false)}
                                onConfirm={matchToPreselectedWork}
                            />
                        ) : (
                            <MatchWorksPopup
                                recordingGroupsCount={recordingGroupsCount}
                                onCancel={() => setModalIsOpen(false)}
                                onContinue={() => goToStep(2)}
                            />
                        )}
                    </ModalPortal>
                </div>
            </div>
        </>
    )
}

export default observer(MatchSearch)
